import React, { Component } from 'react';
import { arrayOf, bool, func, string } from 'prop-types';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { injectIntl } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { propTypes } from '../../util/types';
import { pathByRouteName } from '../../util/routes';
import { getDefaultLocale } from '../../util/genericHelpers';
import { getUrlForLocale } from '../../helpers/convert';

import { Modal } from '../../components';

import css from './ModalMissingInformation.module.css';
import {
  getTermsTimestamp,
  isIndividualUser,
  isUserCreatedWithinDays,
} from '../../util/dataExtractors';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

const MISSING_INFORMATION_MODAL_WHITELIST = [
  'LoginPage',
  'ContactDetailsPage',
  'EmailVerificationPage',
  'PasswordResetPage',
  'StripePayoutPage',
];

const EMAIL_VERIFICATION = 'EMAIL_VERIFICATION';
const STRIPE_ACCOUNT = 'STRIPE_ACCOUNT';
const NEW_USER_SUPPRESSION_DAYS = 7;

export class ModalMissingInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingInformationReminder: null,
      hasSeenMissingInformationReminder: false,
      hasDismissedBanner: false,
    };

    this.handleMissingInformationReminder = this.handleMissingInformationReminder.bind(this);
    this.handleCloseBanner = this.handleCloseBanner.bind(this);
    this.syncBannerDismissalFromUser = this.syncBannerDismissalFromUser.bind(this);
  }

  componentDidMount() {
    this.syncBannerDismissalFromUser(this.props);
  }

  componentDidUpdate(prevProps) {
    const { currentUser, currentUserHasListings, currentUserHasOrders, location } = this.props;
    const user = ensureCurrentUser(currentUser);

    this.handleMissingInformationReminder(
      user,
      currentUserHasListings,
      currentUserHasOrders,
      location
    );

    const prevUser = ensureCurrentUser(prevProps.currentUser);
    const userChanged = (user && user.id) !== (prevUser && prevUser.id);

    if (userChanged || prevProps.location !== this.props.location) {
      this.syncBannerDismissalFromUser(this.props);
    }
  }

  syncBannerDismissalFromUser(props) {
    const { currentUser } = props;
    const user = ensureCurrentUser(currentUser);

    if (user && user.id) {
      const tosVersion = process.env.REACT_APP_TOS || 'default';
      const publicData = user.attributes.profile.publicData || {};
      const hasDismissedBanner = publicData.dismissedTosVersion === tosVersion;

      if (hasDismissedBanner !== this.state.hasDismissedBanner) {
        this.setState({ hasDismissedBanner });
      }
    }
  }

  handleCloseBanner() {
    const { currentUser, onUpdateProfile } = this.props;
    const user = ensureCurrentUser(currentUser);

    if (user && user.id) {
      const tosVersion = process.env.REACT_APP_TOS || 'default';
      onUpdateProfile({ publicData: { dismissedTosVersion: tosVersion } });
    }

    this.setState({ hasDismissedBanner: true });
  }

  handleMissingInformationReminder(
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    newLocation
  ) {
    const routes = this.props.routeConfiguration;
    const currentLocale = getDefaultLocale();

    const whitelistedPaths = MISSING_INFORMATION_MODAL_WHITELIST.map(page =>
      getUrlForLocale(pathByRouteName(page, routes), currentLocale)
    );

    const isPageWhitelisted = whitelistedPaths.includes(newLocation.pathname);
    const pathChanged = newLocation.pathname !== this.props.location.pathname;

    if (!isPageWhitelisted && pathChanged) {
      const hasOrders = currentUserHasOrders === true;
      const hasListingsOrOrders = currentUserHasListings || hasOrders;

      const emailUnverified = !!currentUser.id && !currentUser.attributes.emailVerified;
      const stripeAccountMissing = !!currentUser.id && !currentUser.attributes.stripeConnected;

      if (hasListingsOrOrders && emailUnverified) {
        this.setState({ showMissingInformationReminder: EMAIL_VERIFICATION });
      } else if (currentUserHasListings && stripeAccountMissing) {
        this.setState({ showMissingInformationReminder: STRIPE_ACCOUNT });
      }
    }
  }

  render() {
    const {
      rootClassName,
      className,
      containerClassName,
      currentUser,
      isAuthenticated,
      onManageDisableScrolling,
      routeConfiguration,
      intl,
    } = this.props;

    const user = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!(user && user.id);

    const showTermsAndService = getTermsTimestamp(user) != process.env.REACT_APP_TOS;
    const isNewUser = isUserCreatedWithinDays(user, NEW_USER_SUPPRESSION_DAYS);

    const showBanner =
      isAuthenticated &&
      currentUserLoaded &&
      showTermsAndService &&
      !this.state.hasDismissedBanner &&
      !isNewUser;

    if (!showBanner) {
      return null;
    }

    const currentLocale = getDefaultLocale();

    const privacyUrl = getUrlForLocale(
      pathByRouteName('PrivacyPolicyPage', routeConfiguration),
      currentLocale
    );

    const tosBaseUrl = getUrlForLocale(
      pathByRouteName('TermsOfServicePage', routeConfiguration),
      currentLocale
    );

    const isIndividual = isIndividualUser(user);
    const termsUrl = `${tosBaseUrl}?tab=${isIndividual ? 'consumer' : 'professional'}`;

    return (
      <Modal
        id="MissingInformationReminder"
        containerClassName={classNames(css.bannerModalContainer, containerClassName)}
        isOpen
        onClose={this.handleCloseBanner}
        usePortal
        isTOSModal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={classNames(css.bannerRoot, rootClassName, className)}>
          <div className={css.bannerContent}>
            <span className={css.bannerText}>
              {intl.formatMessage(
                { id: 'ModalMissingInformation.termsBannerMessage' },
                {
                  privacyPolicyLink: (
                    <a href={privacyUrl} className={css.bannerLink}>
                      {intl.formatMessage({
                        id: 'ModalMissingInformation.privacyPolicyLink',
                      })}
                    </a>
                  ),
                  termsOfUseLink: (
                    <a href={termsUrl} className={css.bannerLink}>
                      {intl.formatMessage({
                        id: 'ModalMissingInformation.termsOfUseLink',
                      })}
                    </a>
                  ),
                }
              )}
            </span>
          </div>
        </div>
      </Modal>
    );
  }
}

ModalMissingInformation.propTypes = {
  id: string.isRequired,
  isAuthenticated: bool,
  onManageDisableScrolling: func.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedModalMissingInformation = props => {
  const routeConfiguration = useRouteConfiguration();
  const dispatch = useDispatch();

  return (
    <ModalMissingInformation
      {...props}
      routeConfiguration={routeConfiguration}
      onUpdateProfile={data => dispatch(updateProfile(data))}
    />
  );
};

export default injectIntl(EnhancedModalMissingInformation);
